import { useEntities, useEntity } from "@ui/hooks/serviceUtils";
import useDataManager, { DataManagerProps } from "@ui/hooks/useDataManager";
import { IUser } from "./useProfile";
import { DateTime } from "luxon";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import axios, { AxiosError } from "axios";
import { useToast } from "@chakra-ui/react";

export interface IEmployee extends IUser {
  status?: { id: string; label: string };
  rfid?: string;
  lastWorkingDate?: DateTime | null;
  category: { id: string; label: string };
  rateCode?: { id: string; label: string };
  department?: { id: string; label: string };
  location?: { id: string; label: string };
}

const key = "employee";
const endpoint = "/api/employee";

export const useEmployeesQuery = (props: DataManagerProps<IEmployee> = {}) =>
  useDataManager<IEmployee>(key, `${endpoint}/query`, props);
export const useEmployees = () => useEntities<IEmployee>(key, endpoint);
export const useEmployee = (id: string | undefined, toast: boolean = true) =>
  useEntity<IEmployee>(
    key,
    endpoint,
    id,
    toast
      ? {
          add: "Employee added",
          update: "Employee updated",
          delete: "Employee deleted",
        }
      : undefined,
  );

export const usePasswordReset = (id: string | undefined) => {
  const reset = useMutation({
    mutationFn: async (password: string) => {
      const { data } = await axios.put(`${endpoint}/${id}/reset-password`, {
        password,
      });
      return data;
    },
  });

  return reset;
};

export const useEmployeeSelf = <T extends { id: string }>() =>
  useQuery({
    queryKey: ["employee-me"],
    queryFn: async (): Promise<T> => {
      const { data } = await axios.get("/api/employee/whoami");
      return data;
    },
    staleTime: 5 * 60 * 1000,
    enabled: axios.defaults.headers.common["Authorization"] !== undefined,
  });

export const useBulkUploadEmployeeData = () => {
  const queryClient = useQueryClient();

  const invalidateActive = () => {
    queryClient.invalidateQueries({
      queryKey: [key],
      type: "active",
    });
  };

  const toast = useToast({
    duration: 2000,
    isClosable: true,
    position: "top",
  });

  const upload = useMutation<any, AxiosError<any>, IEmployee[], any>({
    mutationFn: async (masterData) => {
      if (!Array.isArray(masterData)) {
        throw new Error("Bulk upload expects an array of MasterData");
      }

      return await axios.post(`${endpoint}/upload`, masterData);
    },
    onMutate: () => {
      toast.close(`bulk-add-toast-${key}`);
    },
    onSuccess: (entity) => {
      !toast.isActive(`bulk-update-toast-${key}`) &&
        toast({
          title: `Employee added in Bulk successfully`,
          id: `bulk-add-toast-${key}`,
          status: "success",
        });

      queryClient.setQueryData([key], entity);
      invalidateActive();
    },
    onError: (error) => {
      if (!toast.isActive(`bulk-add-error-toast-${key}`)) {
        toast({
          title: error?.response?.data ?? "Error",
          status: "error",
          id: `bulk-add-error-toast-${key}`,
        });
      }
    },
  });

  return {
    upload,
  };
};
